/**
 * SkillCard
 * Displays a skill in a card format with name, description, categories, and actions
 */

import React from 'react';
import { Edit, Trash2, Copy, Lock, Users, Globe } from 'lucide-react';
import { Badge, IconButton } from '../../ui';
import type { Skill } from '../../../types';
import styles from './SkillCard.module.css';

export interface SkillCardProps {
  skill: Skill;
  onEdit?: (skill: Skill) => void;
  onDelete?: (skill: Skill) => void;
  onDuplicate?: (skill: Skill) => void;
  onClick?: (skill: Skill) => void;
}

export const SkillCard: React.FC<SkillCardProps> = ({
  skill,
  onEdit,
  onDelete,
  onDuplicate,
  onClick,
}) => {
  const handleCardClick = () => {
    onClick?.(skill);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(skill);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(skill);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDuplicate?.(skill);
  };

  const getVisibilityIcon = () => {
    switch (skill.visibility) {
      case 'private':
        return <Lock size={14} />;
      case 'project':
        return <Users size={14} />;
      case 'public':
        return <Globe size={14} />;
      default:
        return null;
    }
  };

  const getVisibilityLabel = () => {
    switch (skill.visibility) {
      case 'private':
        return 'Private';
      case 'project':
        return 'Project';
      case 'public':
        return 'Public';
      default:
        return '';
    }
  };

  return (
    <div
      className={styles.card}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardClick();
        }
      }}
    >
      <div className={styles.header}>
        <h3 className={styles.title}>{skill.displayName}</h3>
        <div className={styles.actions}>
          {onEdit && (
            <IconButton
              icon={<Edit size={16} />}
              onClick={handleEdit}
              title="Edit skill"
              size="sm"
            />
          )}
          {onDuplicate && (
            <IconButton
              icon={<Copy size={16} />}
              onClick={handleDuplicate}
              title="Duplicate skill"
              size="sm"
            />
          )}
          {onDelete && (
            <IconButton
              icon={<Trash2 size={16} />}
              onClick={handleDelete}
              title="Delete skill"
              size="sm"
            />
          )}
        </div>
      </div>

      <p className={styles.description}>
        {skill.description.length > 150
          ? `${skill.description.substring(0, 150)}...`
          : skill.description}
      </p>

      {skill.categories && skill.categories.length > 0 && (
        <div className={styles.categories}>
          {skill.categories.map((category) => (
            <Badge key={category} variant="default">
              {category}
            </Badge>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <div className={styles.author}>
          <span className={styles.authorLabel}>by</span>
          <span className={styles.authorName}>{skill.authorName}</span>
        </div>
        <div className={styles.visibility}>
          {getVisibilityIcon()}
          <span>{getVisibilityLabel()}</span>
        </div>
      </div>

      {skill.usageCount > 0 && (
        <div className={styles.usage}>
          Used by {skill.usageCount} {skill.usageCount === 1 ? 'assistant' : 'assistants'}
        </div>
      )}
    </div>
  );
};

export default SkillCard;
